import { useQuery } from '@tanstack/react-query'
import {
  Outlet,
  createFileRoute,
  useRouteContext,
} from '@tanstack/react-router'
import { useEffect } from 'react'
import { useInterval } from 'usehooks-ts'

import * as Sentry from '@sentry/react'

import { meQueryOptions } from '@/graphql/query-options'
import { LoadingScreen } from '@/components/shared/loading-logo'
import { storeAuth } from '@/lib/auth'
import { setAbilities } from '@/lib/rbac'
import { useGlobalLogout } from '@/hooks/use-global-logout'

export const Route = createFileRoute('/_auth')({
  beforeLoad: async ({ context, location }) => {
    if (!context.auth.isAuthenticated) return
    if (['/login-success', '/logout'].includes(location.pathname)) return

    const data = await context.queryClient.ensureQueryData(meQueryOptions())

    if (!data.me) return

    setAbilities(data.me.heartAbilities.map((a) => a.identifier))

    Sentry.setUser({
      id: data.me.id,
      email: data.me.email ?? undefined,
      username: data.me.fullName ?? undefined,
    })
  },
  component: AuthLayout,
})

const REFRESH_BUFFER = 10 * 60 * 1000 // 10 min

function AuthLayout() {
  const { auth } = useRouteContext({ from: '__root__' })
  const {
    isAuthenticated,
    loginWithRedirect,
    isLoading,
    error,
    getAccessTokenSilently,
  } = auth

  const logout = useGlobalLogout()

  useInterval(
    async () => {
      try {
        const token = await getAccessTokenSilently()
        storeAuth(token)
      } catch (e) {
        console.error('Auth0 interval error', e)
        logout()
      }
    },
    isAuthenticated ? REFRESH_BUFFER : null,
  )

  if (error) {
    console.error('Auth0 error', error)
  }

  if (!isAuthenticated) {
    loginWithRedirect({
      appState: {
        returnTo: window.location.pathname,
      },
    })
  }

  // GET_ME errors are handled by the global error boundary
  // Keep me subscribed so refetches surface errors and abilities stay fresh.
  // Cache is already populated by beforeLoad; this reuses it.
  const { data, isPending: meLoading } = useQuery({
    ...meQueryOptions(),
    enabled: isAuthenticated,
    throwOnError: true,
  })

  useEffect(() => {
    if (data?.me) {
      setAbilities(data.me.heartAbilities.map((a) => a.identifier))
    }
  }, [data])

  if (isLoading || error || !isAuthenticated || meLoading) {
    return <LoadingScreen />
  }

  return <Outlet />
}
